import { useState, useEffect, useCallback, useId, useRef } from 'react';
import { cn } from '../cn';
import type { MediaCategory } from '@fanini/shared-types';

// ── Types ────────────────────────────────────────────────────────────────────

/** Minimaler Asset-Typ für den Picker. */
export interface PickerAsset {
  id: string;
  publicUrl: string | null;
  originalName: string;
  mimeType: string;
  category: MediaCategory;
}

/** Konfiguration einer Kategorie-Tab im Picker. */
export interface PickerCategoryConfig {
  id: MediaCategory;
  label: string;
}

/**
 * Props für den AssetPickerDialog.
 */
export interface AssetPickerDialogProps {
  /** Ob der Dialog sichtbar ist. */
  open: boolean;
  /** Callback zum Schließen des Dialogs. */
  onClose: () => void;
  /**
   * Callback wenn ein Asset ausgewählt wurde.
   * Erhält die `publicUrl` des gewählten Assets.
   */
  onSelect: (url: string) => void;
  /**
   * Erlaubte Kategorien. Wenn angegeben, werden nur diese Kategorien angezeigt.
   * Standard: alle Brand-Kategorien.
   */
  allowedCategories?: MediaCategory[];
  /**
   * Vorausgewählte Kategorie.
   * Standard: erste erlaubte Kategorie.
   */
  defaultCategory?: MediaCategory;
  /**
   * Funktion zum Laden der Assets einer Kategorie.
   * Wird bei Kategorie-Wechsel und beim Öffnen aufgerufen.
   * Muss vom App-Layer bereitgestellt werden (Dependency Inversion).
   */
  fetchAssets: (category: MediaCategory) => Promise<PickerAsset[]>;
}

// ── Kategorie-Konfiguration ──────────────────────────────────────────────────

const ALL_BRAND_CATEGORIES: PickerCategoryConfig[] = [
  { id: 'brand-logo', label: 'Logos' },
  { id: 'brand-icon', label: 'Icons' },
  { id: 'brand-brush-stroke', label: 'Pinselstriche' },
  { id: 'brand-ripped-paper', label: 'Ripped Paper' },
  { id: 'brand-background', label: 'Hintergründe' },
  { id: 'brand-misc', label: 'Sonstiges' },
];

// ── AssetPickerDialog ────────────────────────────────────────────────────────

/**
 * Modal-Dialog zur Auswahl eines Assets aus der Brand-Bibliothek.
 * Zeigt Kategorie-Tabs, Suchfeld und klickbares Thumbnail-Raster.
 *
 * Dependency Inversion: Die `fetchAssets`-Funktion wird vom App-Layer injiziert,
 * sodass shared-ui keine direkte Abhängigkeit auf API-Layer hat.
 *
 * @param props.open - Ob der Dialog sichtbar ist
 * @param props.onClose - Schließen-Callback
 * @param props.onSelect - Auswahl-Callback (erhält publicUrl)
 * @param props.allowedCategories - Erlaubte Kategorien (optional, Standard: alle Brand-Kategorien)
 * @param props.defaultCategory - Vorausgewählte Kategorie
 * @param props.fetchAssets - Funktion zum Laden der Assets (App-Layer-Abhängigkeit)
 * @example
 * ```tsx
 * <AssetPickerDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSelect={(url) => setImageUrl(url)}
 *   allowedCategories={['brand-logo']}
 *   fetchAssets={async (category) => {
 *     const res = await apiFetch<{ items: MediaFile[] }>(`/media?category=${category}&pageSize=100`);
 *     return res.items;
 *   }}
 * />
 * ```
 */
export function AssetPickerDialog({
  open,
  onClose,
  onSelect,
  allowedCategories,
  defaultCategory,
  fetchAssets,
}: AssetPickerDialogProps) {
  const titleId = useId();

  // Sichtbare Kategorien filtern
  const visibleCategories = allowedCategories
    ? ALL_BRAND_CATEGORIES.filter((c) => allowedCategories.includes(c.id))
    : ALL_BRAND_CATEGORIES;

  const isDefaultValid = defaultCategory && visibleCategories.some((c) => c.id === defaultCategory);
  const firstCategory = isDefaultValid
    ? defaultCategory
    : (visibleCategories[0]?.id ?? 'brand-logo');

  const [activeCategory, setActiveCategory] = useState<MediaCategory>(firstCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [assets, setAssets] = useState<PickerAsset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Assets laden wenn Kategorie wechselt oder Dialog öffnet
  const loadAssets = useCallback(
    async (category: MediaCategory) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchAssets(category);
        setAssets(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Laden der Assets.');
        setAssets([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchAssets],
  );

  // Beim Öffnen: Kategorie zurücksetzen.
  // Das Laden übernimmt der activeCategory-Effect.
  const firstCategoryRef = useRef(firstCategory);
  firstCategoryRef.current = firstCategory;
  useEffect(() => {
    if (open) {
      setActiveCategory(firstCategoryRef.current);
      setSearchQuery('');
    }
  }, [open]);

  // Assets laden wenn Kategorie wechselt (auch beim Öffnen, da open als Dep).
  useEffect(() => {
    if (open) {
      void loadAssets(activeCategory);
    }
  }, [activeCategory, open, loadAssets]);

  // Escape-Taste
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Body-Scroll sperren
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const filteredAssets = searchQuery.trim()
    ? assets.filter((a) => a.originalName.toLowerCase().includes(searchQuery.toLowerCase()))
    : assets;

  function handleCategoryChange(category: MediaCategory) {
    setActiveCategory(category);
    setSearchQuery('');
  }

  function handleSelect(asset: PickerAsset) {
    if (!asset.publicUrl) return;
    onSelect(asset.publicUrl);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4"
      role="presentation"
    >
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 flex flex-col w-full max-w-3xl max-h-[85vh] bg-card border border-ink/10 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink/10 flex-shrink-0">
          <h2 id={titleId} className="text-sm font-black uppercase tracking-wide text-ink">
            Asset aus Bibliothek wählen
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Dialog schließen"
            className="inline-flex items-center justify-center w-7 h-7 text-ink-muted hover:text-ink hover:bg-ink/5 transition-colors duration-100"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Kategorie-Tabs */}
        <div
          className="flex gap-0 border-b border-ink/10 overflow-x-auto flex-shrink-0"
          role="tablist"
          aria-label="Asset-Kategorien"
        >
          {visibleCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={cn(
                'px-4 py-2.5 text-xs font-bold uppercase tracking-wide whitespace-nowrap border-b-2 transition-colors duration-150',
                activeCategory === cat.id
                  ? 'border-brand text-brand bg-brand/5'
                  : 'border-transparent text-ink-muted hover:text-ink hover:bg-ink/5',
              )}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Suchfeld */}
        <div className="px-5 py-3 border-b border-ink/10 flex-shrink-0">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Dateiname suchen…"
            aria-label="Assets suchen"
            className="w-full px-3 py-1.5 text-sm bg-paper border border-ink/10 text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand/50 focus:ring-1 focus:ring-brand/20 transition-colors duration-150"
          />
        </div>

        {/* Asset-Raster */}
        <div
          className="flex-1 overflow-y-auto p-5"
          role="tabpanel"
          aria-label={`Assets der Kategorie ${visibleCategories.find((c) => c.id === activeCategory)?.label ?? activeCategory}`}
        >
          {isLoading && (
            <div className="flex items-center justify-center h-40">
              <span
                className="h-8 w-8 border-2 border-brand border-t-transparent rounded-full animate-spin"
                aria-label="Lädt…"
              />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex items-center justify-center h-40">
              <p className="text-sm text-bad">{error}</p>
            </div>
          )}

          {!isLoading && !error && filteredAssets.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-ink-muted">
              <svg
                className="h-10 w-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm">
                {searchQuery
                  ? `Keine Treffer für "${searchQuery}"`
                  : 'Keine Assets in dieser Kategorie.'}
              </p>
            </div>
          )}

          {!isLoading && !error && filteredAssets.length > 0 && (
            <div
              className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3"
              aria-label="Asset-Auswahl"
            >
              {filteredAssets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => handleSelect(asset)}
                  disabled={!asset.publicUrl}
                  title={asset.originalName}
                  aria-label={`${asset.originalName} auswählen`}
                  className="group flex flex-col gap-1.5 bg-paper border border-ink/10 p-2 text-left hover:border-brand/60 hover:bg-brand/5 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {/* Thumbnail */}
                  <div className="aspect-square flex items-center justify-center bg-ink/5 overflow-hidden">
                    {asset.publicUrl && asset.mimeType.startsWith('image/') ? (
                      <img
                        src={asset.publicUrl}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-contain p-1.5"
                        loading="lazy"
                      />
                    ) : (
                      <svg
                        className="h-6 w-6 text-ink-muted"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Dateiname */}
                  <p className="text-[10px] text-ink-muted group-hover:text-ink truncate leading-tight transition-colors duration-150">
                    {asset.originalName}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-ink/10 flex-shrink-0">
          <p className="text-xs text-ink-muted">
            {!isLoading && `${filteredAssets.length} Assets`}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-ink-muted hover:text-ink border border-ink/10 hover:border-ink/20 transition-colors duration-150"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
